import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react';
import useAuthStore from '../../stores/authStore';

function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    await login(email, password);
  };

  return (
    <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl shadow-elevated overflow-hidden">
      {/* Header with accent bar */}
      <div className="relative px-8 pt-8 pb-6">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[var(--color-steel-dark)] via-[var(--color-steel)] to-[var(--color-steel-dark)]" />
        <h2 className="text-xl font-semibold text-[var(--foreground)] text-center">{t('auth.login')}</h2>
        <p className="text-sm text-[var(--muted-foreground)] text-center mt-1">Connectez-vous à votre compte</p>
      </div>

      {/* Form */}
      <div className="px-8 pb-8">
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 rounded-lg bg-[var(--color-brick-bg)] border border-[var(--color-brick-border)] text-[var(--color-brick)] text-sm flex items-center gap-2">
              <div className="w-2 h-2 bg-[var(--color-brick)] rounded-full flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[var(--muted-foreground)] mb-1.5">{t('auth.email')}</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="w-5 h-5 text-[var(--muted-foreground)]" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-[var(--input-background)] border border-[var(--switch-background)] rounded-lg text-[var(--foreground)] placeholder-[var(--muted-foreground-light)] focus:border-[var(--color-steel)] focus:ring-2 focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all text-sm"
                placeholder="nom@exemple.com"
                required
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[var(--muted-foreground)] mb-1.5">{t('auth.password')}</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="w-5 h-5 text-[var(--muted-foreground)]" />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 bg-[var(--input-background)] border border-[var(--switch-background)] rounded-lg text-[var(--foreground)] placeholder-[var(--muted-foreground-light)] focus:border-[var(--color-steel)] focus:ring-2 focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all text-sm"
                placeholder="&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-[var(--border)] bg-[var(--input-background)] text-[var(--color-steel)] accent-[var(--color-steel)]"
              />
              <span className="text-sm text-[var(--muted-foreground)]">{t('auth.rememberMe')}</span>
            </label>
            <a href="#" className="text-sm text-[var(--color-steel)] hover:text-[var(--color-steel-light)] transition-colors">{t('auth.forgotPassword')}</a>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-[var(--color-steel)] hover:bg-[var(--color-steel-hover)] text-white font-medium rounded-lg btn-primary-glow transition-all disabled:opacity-50 disabled:pointer-events-none text-sm"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('auth.loggingIn')}
              </>
            ) : (
              t('auth.loginButton')
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
